import { CheckCircle, XCircle } from "../../../components/icons/svg/outline";
import { getJobActiveStatusLabel } from "../../../utils/business";
import { getUserDisplayName } from "../../../utils/business/user";

/**
 * Return status chip config from the JobNumber active/enabled flag.
 *
 * @param {boolean} enabled - Whether the backing JobNumber is active/enabled.
 * @returns {{ label: string, Icon: React.ComponentType<{className?: string}>, className: string }}
 */
export function getProjectStatusConfig(enabled) {
  if (enabled) {
    return {
      label: getJobActiveStatusLabel(enabled),
      Icon: CheckCircle,
      className: "border-primary bg-primary text-white",
    };
  }
  return {
    label: getJobActiveStatusLabel(enabled),
    Icon: XCircle,
    className: "border-gray-200 bg-gray-100 text-gray-600",
  };
}

/** Shared chip chrome for status + manager pills on Projects Home surfaces. */
export const PROJECT_HOME_CHIP_CLASS =
  "inline-flex h-4 items-center gap-0.5 rounded-full border px-1.5 text-[10px] font-normal leading-none";

/** Manager pills — primary green fill matching card header. */
export const PROJECT_HOME_MANAGER_CHIP_CLASS =
  "border-primary bg-primary text-white";

/**
 * Active status chip on a primary-green surface (e.g. card header) —
 * white fill so it stays readable against ``bg-primary``.
 */
export const PROJECT_HOME_STATUS_ON_PRIMARY_CLASS =
  "border-white/40 bg-white text-primary";

/**
 * Normalize a project/API date value to ``YYYY-MM-DD`` (or null when blank).
 *
 * Uses a string slice instead of {@link toLocalDateKey}: date-only API values
 * like ``YYYY-MM-DD`` parse as UTC in ``Date`` and can shift a day in PT.
 *
 * @param {string|null|undefined} value - Raw date field.
 * @returns {string|null}
 */
function toProjectDateKey(value) {
  if (value == null || value === "") {
    return null;
  }
  return String(value).split("T")[0];
}

/**
 * Whether a project matches the Projects Home start/end date filter.
 *
 * - Both empty: inactive (always matches).
 * - Both set: project must have both dates and be fully contained (inclusive).
 * - Start only: ``date_start >= filterStart``; ignore ``date_end``.
 * - End only: ``date_end <= filterEnd``; ignore ``date_start``.
 * - Both set with start > end: no matches.
 *
 * @param {Object} project - Project API row.
 * @param {string} filterStart - Filter start (`YYYY-MM-DD` or empty).
 * @param {string} filterEnd - Filter end (`YYYY-MM-DD` or empty).
 * @returns {boolean}
 */
export function projectMatchesDateRangeFilter(project, filterStart, filterEnd) {
  const hasStart = Boolean(filterStart);
  const hasEnd = Boolean(filterEnd);
  if (!hasStart && !hasEnd) {
    return true;
  }
  if (hasStart && hasEnd && filterStart > filterEnd) {
    return false;
  }

  const projectStart = toProjectDateKey(project?.date_start);
  const projectEnd = toProjectDateKey(project?.date_end);

  if (hasStart && hasEnd) {
    if (!projectStart || !projectEnd) {
      return false;
    }
    return projectStart >= filterStart && projectEnd <= filterEnd;
  }

  if (hasStart) {
    return Boolean(projectStart) && projectStart >= filterStart;
  }

  return Boolean(projectEnd) && projectEnd <= filterEnd;
}

/**
 * Build sorted customer multi-select options from loaded projects.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildProjectCustomerFilterOptions(projects) {
  const byId = new Map();
  for (const project of projects || []) {
    const id = project?.customer?.id;
    const name = project?.customer?.name;
    if (id == null || !name) {
      continue;
    }
    byId.set(String(id), name);
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build sorted manager multi-select options from loaded projects.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildProjectManagerFilterOptions(projects) {
  const byId = new Map();
  for (const project of projects || []) {
    for (const manager of project?.managers || []) {
      if (manager?.id == null) {
        continue;
      }
      const label = getUserDisplayName(manager, "");
      if (!label) {
        continue;
      }
      byId.set(String(manager.id), label);
    }
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Whether a project matches the selected customer filter (empty = inactive).
 *
 * @param {Object} project - Project API row.
 * @param {Array<string>} selectedCustomerIds - Selected customer id strings.
 * @returns {boolean}
 */
export function projectMatchesCustomerFilter(project, selectedCustomerIds) {
  if (!selectedCustomerIds?.length) {
    return true;
  }
  const customerId = project?.customer?.id;
  if (customerId == null) {
    return false;
  }
  return selectedCustomerIds.includes(String(customerId));
}

/**
 * Whether a project matches the selected manager filter (OR: any selected manager).
 *
 * @param {Object} project - Project API row.
 * @param {Array<string>} selectedManagerIds - Selected manager id strings.
 * @returns {boolean}
 */
export function projectMatchesManagerFilter(project, selectedManagerIds) {
  if (!selectedManagerIds?.length) {
    return true;
  }
  const managerIds = (project?.managers || [])
    .map((manager) => (manager?.id == null ? null : String(manager.id)))
    .filter(Boolean);
  return selectedManagerIds.some((id) => managerIds.includes(id));
}
